import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtOtherPeriodsButton } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentDistributionMarginMixQueryOptions,
  distributionMarginMixDetailsExportQueryOptions,
  distributionMarginMixDetailsRawExportQueryOptions,
  distributionMarginMixDetailsSearchSchema,
  useDistributionMarginMixDetailsViewModel,
  useUpdateDistributionMarginMix,
} from '@/hooks/queries/crt/useDistributionMarginMix'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { UpdateDistributionMarginMix } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DistributionMarginMixConfigurationMenu } from './-components/ConfigurationMenu'
import DistributionMarginMixDetailsDataTable from './-components/DistributionMarginMixDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/data-sources/distribution-margin-mix/$year/$month',
)({
  component: DistributionMarginMixPeriod,
  validateSearch: (search) => distributionMarginMixDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const record = crtPeriod
      ? await queryClient.fetchQuery(currentDistributionMarginMixQueryOptions(crtPeriod))
      : null

    return {
      crumb: record?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      distributionMarginMixId: record ? String(record.id) : null,
    }
  },
})

function DistributionMarginMixPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, distributionMarginMixId } = Route.useLoaderData()

  if (!distributionMarginMixId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/customer-investments/data-sources/distribution-margin-mix"
      />
    )
  }

  return (
    <DistributionMarginMixDetails
      key={distributionMarginMixId}
      distributionMarginMixId={distributionMarginMixId}
    />
  )
}

function DistributionMarginMixDetails({
  distributionMarginMixId,
}: {
  distributionMarginMixId: string
}) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useDistributionMarginMixDetailsViewModel(
    distributionMarginMixId,
    {
      ...search,
      page: search.page ?? 1,
      pageSize,
    },
  )

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateDistributionMarginMix | null => {
    if (!data) return null

    const changes: UpdateDistributionMarginMix = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const { mutate: update, isPending: isUpdatePending } = useUpdateDistributionMarginMix({
    distributionMarginMixId: Number(distributionMarginMixId),
  })

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    update(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.distributionMarginMix.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={(name) => setFormState((prev) => ({ ...prev, name }))}
          onUpdateDescription={(description) => setFormState((prev) => ({ ...prev, description }))}
          permission="crt_distribution_margin_mix_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>
          <HStack gap="sm">
            <DistributionMarginMixConfigurationMenu
              distributionMarginMixId={distributionMarginMixId}
            />
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      distributionMarginMixDetailsExportQueryOptions(
                        distributionMarginMixId,
                        search,
                      ),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      distributionMarginMixDetailsRawExportQueryOptions(distributionMarginMixId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                />,
              ]}
            />
            <CrtOtherPeriodsButton otherPeriodsTo="/crt/customer-investments/data-sources/distribution-margin-mix" />
          </HStack>
        </VStack>
      </HStack>

      <DistributionMarginMixDetailsDataTable
        distributionMarginMixId={distributionMarginMixId}
        distributionMarginMix={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_distribution_margin_mix_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
